import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { Send as SendIcon, SmartToy as BotIcon } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import {
  fetchChatHistory as fetchChatHistoryApi,
  sendChatMessage,
} from "../../apis/chat-apis/chatApis";
import { useAuth0 } from "@auth0/auth0-react";

function simpleSimilarity(userInput, questions) {
  const userWords = userInput.toLowerCase().split(/\s+/);

  let bestMatchIndex = -1;
  let bestScore = 0;

  questions.forEach((question, index) => {
    const questionWords = question.toLowerCase().split(/\s+/);

    const commonWords = userWords.filter((word) =>
      questionWords.includes(word)
    );

    const score = commonWords.length / questionWords.length;

    if (score > bestScore) {
      bestScore = score;
      bestMatchIndex = index;
    }
  });

  return { bestMatchIndex, bestScore };
}

const hardcodedAnswers = {
  "how does your organization manage access to critical systems?": `
Our organization manages access to critical systems through a structured Access Control Policy designed to uphold the principles of least privilege, need-to-know, and compliance with standards such as SOC 2, ISO 27001, HIPAA, and NIST 800-53.

Key controls include:
- Role-Based Access Control (RBAC): Access is granted based on job roles and security profiles, following an approval workflow involving department heads and IT Security.
- User Provisioning & Deprovisioning: New access requests must be approved before provisioning. Access is revoked immediately upon employee termination or role change. Temporary access is time-bound.
- Multi-Factor Authentication (MFA): MFA is mandatory for all users accessing critical systems, ensuring an added layer of security.
- Privileged Access Management (PAM): Privileged accounts are tightly controlled, requiring additional authentication, and all activities are logged and reviewed monthly.
- Periodic Reviews: Access rights are reviewed at least quarterly; privileged accounts undergo monthly audits to ensure appropriate usage.
- Third-Party Access Controls: Vendor access is limited to business necessity, governed by security agreements, and reviewed quarterly.
- Remote Access Controls: All remote access requires VPN, MFA, and device compliance with security standards. Session timeouts are enforced.
- Logging and Monitoring: All access to critical systems is logged and monitored. Alerts are configured for unauthorized attempts, and logs are retained for at least one year.

This layered approach ensures that only authorized personnel have appropriate access to systems and data, with robust mechanisms for review, monitoring, and enforcement.
`,

  "how does your organization ensure sensitive data is protected?": `
Our organization takes a multi-layered and proactive approach to protecting sensitive data. Key strategies include:

Access and Identity Controls:
- Role-Based Access Control (RBAC) and the principle of least privilege are enforced.
- Multi-Factor Authentication (MFA) is required for access to critical systems.
- Access is regularly reviewed and revoked promptly upon employment termination.

Data Classification and Encryption:
- Sensitive data is classified into Confidential, Restricted, and Public categories.
- Confidential data (e.g., customer data, financials, IP) is encrypted at rest and in transit using standards like AES-256 and TLS.
- A centralized key management system ensures only authorized personnel can access encryption keys.

Infrastructure and Endpoint Security:
- Firewalls, IDS/IPS, and endpoint protection tools (e.g., antivirus, EDR) are deployed.
- Network segmentation and Zero Trust principles prevent lateral movement within systems.

Monitoring and Threat Detection:
- Centralized Security Information and Event Management (SIEM) tools are used to monitor access, detect anomalies, and issue real-time alerts.
- Behavioral analytics help identify suspicious patterns.

Operational and Procedural Safeguards:
- A Secure Software Development Lifecycle (SDLC) incorporates secure coding and vulnerability scanning.
- Data Loss Prevention (DLP) tools monitor and prevent unauthorized transfers of sensitive data.
- Remote work is secured through VPNs, encrypted communications, and endpoint hardening.

People and Process Controls:
- All employees undergo security awareness training, including phishing simulations.
- Confidentiality agreements and background checks are required for staff and contractors.
- Security responsibilities are clearly defined for all roles.

Third-Party Risk Management:
- Third-party access is tightly controlled and granted only after due diligence and under strict contractual security obligations.
- Vendor security is regularly assessed through audits and ongoing monitoring.
`,

  "provide a list of all people related controls with failed checks?": `
Following is a list of all people related controls with failed checks:
- RC6: New Hire Policy Acknowledgement
- RC12: Periodic Policy Acknowledgement
- RC35: Termination of employment
`,

  "provide details of what's failing for control rc6": `
For RC6, the following policies were not acknowledged by the new joiners within 30 days of joining:
- Risk management policy
- Asset management policy
- Data classification policy
- Incident management policy
- Cryptography policy
- Vulnerability and patch management policy
- Privacy Policy
`,

  "what steps should be taken to fix this issue?": `
Steps to fix this issue:
1. Send automated reminders to employees who have not yet acknowledged the policy.
2. Follow up with manual communication (e.g., direct email or manager notification) if automated efforts fail.
3. Set a clear and firm deadline for acknowledgment and state consequences of noncompliance (e.g., restricted system access, HR action etc).
`,

  "how many active employees do you have in your organization?": "4",

  "have all the active employees acknowledged the code of conduct policy? provide a list of employees who have not": `
None of the active employees have acknowledged the code of conduct policy.
`,
};

//TODO: clean this up, made at a very fast pace, redudant code blocks
// Create a theme matching the image's style
const theme = createTheme({
  palette: {
    primary: {
      main: "#6949FF",
    },
    background: {
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          borderRadius: 20,
        },
      },
    },
  },
});

const ChatAssistantUI = () => {
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(
    "560355fe-138d-4011-a8bb-aa7443cc35d3"
  );
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  useEffect(() => {
    fetchChatHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChatHistory = async () => {
    try {
      setLoading(true);
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;

      const response = await fetchChatHistoryApi(
        idToken,
        accessToken,
        sessionId
      );

      if (response) {
        setMessages(response);
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!input.trim()) return;
    const userInput = input.trim().toLowerCase();
    // Optimistically add user message to UI
    const userMessage = { role: "user", content: input };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setInput("");

    const questions = Object.keys(hardcodedAnswers);
    const { bestMatchIndex, bestScore } = simpleSimilarity(
      userInput,
      questions
    );

    if (bestScore > 0.5) {
      setLoading(true);

      setTimeout(() => {
        setMessages((prevMessages) => [
          ...prevMessages,
          { role: "bot", content: hardcodedAnswers[questions[bestMatchIndex]] },
        ]);
        setLoading(false);
      }, 4000);

      return;
    }

    setLoading(true); // Continue to fallback (API or other)

    try {
      // Get the tokens
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;

      const response = await sendChatMessage(
        idToken,
        accessToken,
        sessionId,
        input
      );

      const data = await response;

      // Add bot response to messages
      if (data) {
        setMessages((prevMessages) => [
          ...prevMessages,
          { role: "bot", content: data },
        ]);
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          { role: "bot", content: "Failed to generate response" },
        ]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prevMessages) => [
        ...prevMessages,
        { role: "bot", content: "An error occurred. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  // Format message content with proper line breaks
  const formatMessage = (content) => {
    console.log(content);
    return typeof content !== "string"
      ? content
      : content.split("\n").map((line, index) => (
          <React.Fragment key={index}>
            {line}
            <br />
          </React.Fragment>
        ));
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "80vh",
          width: "100%",
          backgroundColor: "background.default",
          overflow: "hidden", // Prevent outer scroll
        }}
        id="someid"
      >
        {/* Messages Area */}
        <Box
          ref={chatContainerRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {loading && messages.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ width: "100%", mt: 2, mb: 4 }}>
              {messages.length === 0 && (
                <Box sx={{ mb: 2 }}>
                  <BotMessage content="Hi, I'm your Chat Assistant. How can I help you today?" />
                </Box>
              )}

              {messages.map((message, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  {message.role === "user" ? (
                    <UserMessage content={message.content} />
                  ) : (
                    <BotMessage content={message.content} />
                  )}
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        {/* Input Area */}
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid rgba(0,0,0,0.05)",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              maxWidth: "800px",
              mx: "auto",
              position: "relative",
              width: "100%",
            }}
          >
            <form onSubmit={handleSendMessage}>
              <TextField
                fullWidth
                multiline
                placeholder="Send a message..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                variant="outlined"
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "20px",
                    backgroundColor: "background.paper",
                    pr: "48px", // Make room for the send button
                  },
                }}
              />
              <IconButton
                color="primary"
                type="submit"
                disabled={loading || !input.trim()}
                sx={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  p: "8px",
                }}
              >
                {loading ? <CircularProgress size={24} /> : <SendIcon />}
              </IconButton>
            </form>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

// Bot message component
const BotMessage = ({ content }) => {
  return (
    <Box
      sx={{
        display: "flex",
        //   px: 3, // Add padding to the container instead of the messages
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 2,
          maxWidth: "80%",
          backgroundColor: "#F5F5FA",
          p: 2,
          borderRadius: "16px",
          borderTopRightRadius: "4px",
          maxWidth: "80%",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box
          sx={{
            p: 3,
            borderRadius: "16px",
            borderTopLeftRadius: "4px",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "#6949FF",
              lineHeight: 1.5,
            }}
          >
            {content.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                {line}
                <br />
              </React.Fragment>
            ))}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// User message component
const UserMessage = ({ content }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        marginRight: 2,
        width: "100%",
      }}
    >
      <Box
        sx={{
          backgroundColor: "#F5F5FA",
          p: 2,
          borderRadius: "16px",
          borderTopRightRadius: "4px",
          maxWidth: "80%",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
            lineHeight: 1.5,
          }}
        >
          {content}
        </Typography>
      </Box>
    </Box>
  );
};

export default ChatAssistantUI;
